import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Activity, Database, Rocket, CreditCard, Webhook, Mail, Loader2,
  CheckCircle2, XCircle, RefreshCw, Layers, Send,
} from "lucide-react";
import { toast } from "sonner";

/* Compact production-diagnostic tile that reads /api/health/detailed. Admin-only
   route already, so no separate gating here. Green/red dots for each critical
   service, migration ladder, and estimated collection counts. */

const fmtRel = (iso) => {
  if (!iso) return "—";
  try {
    const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return "—"; }
};

const Dot = ({ ok, label, Icon, sub, testid }) => (
  <div
    className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/10"}`}
    data-testid={testid}
  >
    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-400" : "text-rose-400"}`} />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">{label}</span>
        {ok ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-rose-400" />}
      </div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</div>}
    </div>
  </div>
);

// A small, dense "chip" for the counts row.
const CountChip = ({ label, value }) => (
  <div className="rounded-md border border-slate-800 bg-slate-900/60 px-2.5 py-1.5 flex flex-col items-start">
    <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <span className="text-sm font-mono-clinical text-slate-200">{typeof value === "number" ? value.toLocaleString() : "—"}</span>
  </div>
);

export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState(null);
  const [err, setErr] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setReloading(true);
    try {
      const [health, emailStatus] = await Promise.all([
        api.get("/api/health/detailed"),
        api.get("/api/admin/email/status").catch(() => ({ data: null })),
      ]);
      setData(health.data);
      setEmail(emailStatus.data);
      setErr(false);
    } catch {
      setErr(true);
    } finally {
      setReloading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/api/admin/email/test");
      if (data.ok) {
        toast.success(`Test email sent to ${data.to}`);
      } else {
        toast.error("Test email failed", { description: data.error || "See recent log below." });
      }
      // reload to reflect the new attempt in the log
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(status === 503 ? "Email is not configured" : "Test email failed", {
        description: msg || "See backend logs for details.",
      });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] overflow-hidden" data-testid="system-health-card">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#5ec0ff]" />
          <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">System health</span>
          {data?.generated_at && (
            <span className="text-[10px] text-slate-500 ml-1" data-testid="system-health-timestamp">
              · updated {fmtRel(data.generated_at)}
            </span>
          )}
        </div>
        <button
          onClick={load}
          disabled={reloading}
          className="text-[11px] text-[#5ec0ff] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="system-health-refresh-btn"
          aria-label="Refresh health"
        >
          <RefreshCw className={`w-3 h-3 ${reloading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="p-4">
        {data === null && !err ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading system health…
          </div>
        ) : err ? (
          <div className="text-center text-rose-400 text-sm py-6" data-testid="system-health-error">
            Couldn&apos;t reach /api/health/detailed. Try Refresh.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <Dot
                ok={data.db.ok}
                label="Database"
                Icon={Database}
                sub={data.db.ok ? `${data.db.latency_ms} ms · ${data.db.name}` : "unreachable"}
                testid="health-dot-db"
              />
              <Dot
                ok={data.bootstrap.ok}
                label="Bootstrap"
                Icon={Rocket}
                sub={data.bootstrap.ok ? fmtRel(data.bootstrap.completed_at) : (data.bootstrap.last_error || "pending")}
                testid="health-dot-bootstrap"
              />
              <Dot
                ok={data.integrations.stripe_enabled}
                label="Stripe"
                Icon={CreditCard}
                sub={data.integrations.stripe_enabled ? "live" : "disabled"}
                testid="health-dot-stripe"
              />
              <Dot
                ok={data.integrations.stripe_webhook_secret_configured}
                label="Webhook secret"
                Icon={Webhook}
                sub={data.integrations.stripe_webhook_secret_configured ? "configured" : "MISSING"}
                testid="health-dot-webhook"
              />
            </div>

            {/* migrations ladder + email flag */}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="health-migrations">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-3.5 h-3.5 text-[#5ec0ff]" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                    Migrations · v{data.migrations.latest_version ?? "?"}
                  </span>
                  {data.migrations.pending_count > 0 ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 font-semibold">
                      {data.migrations.pending_count} pending
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 font-semibold">
                      all applied
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {(data.migrations.applied || []).map((m) => (
                    <div key={m.version} className="flex items-center justify-between text-[11px]" data-testid={`migration-v${m.version}`}>
                      <span className="text-slate-300">
                        <span className="font-mono-clinical text-[#5ec0ff]">v{m.version}</span>
                        <span className="text-slate-500"> · </span>
                        <span>{m.name}</span>
                      </span>
                      <span className="text-slate-500">{fmtRel(m.applied_at)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Dot
                  ok={data.integrations.email_enabled}
                  label="Email (Resend)"
                  Icon={Mail}
                  sub={data.integrations.email_enabled ? "configured" : "not configured"}
                  testid="health-dot-email"
                />
                <div className="text-[10px] text-slate-500 px-1">
                  Admin seed: {data.seed.admin_seeded ? "present ✓" : "MISSING"}
                </div>
              </div>
            </div>

            {/* email deliverability drill-down */}
            {email && (
              <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="health-email-deliverability">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-[#5ec0ff] shrink-0" />
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Email deliverability · 24 h</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 font-semibold" data-testid="email-total-sent">
                      {email.totals_24h.sent} sent
                    </span>
                    {email.totals_24h.failed > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 font-semibold" data-testid="email-total-failed">
                        {email.totals_24h.failed} failed
                      </span>
                    )}
                    {email.sender && (
                      <span className="text-[10px] text-slate-500 font-mono-clinical truncate">from {email.sender}</span>
                    )}
                  </div>
                  <button
                    onClick={sendTest}
                    disabled={sendingTest || !email.enabled}
                    className="text-[11px] text-[#5ec0ff] hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="email-send-test-btn"
                  >
                    {sendingTest
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Send className="w-3 h-3" />}
                    Send test to me
                  </button>
                </div>

                {email.last_error && (
                  <div className="mb-2 rounded border border-rose-500/30 bg-rose-500/5 px-2 py-1.5 text-[11px] text-rose-300" data-testid="email-last-error">
                    <span className="font-semibold">Last error · {email.last_error.kind}: </span>
                    <span className="font-mono-clinical text-rose-200/90 break-all">{email.last_error.error}</span>
                    <span className="text-slate-500 ml-1">({fmtRel(email.last_error.created_at)})</span>
                  </div>
                )}

                {email.recent.length === 0 ? (
                  <div className="text-[11px] text-slate-500 py-1">No email attempts recorded yet.</div>
                ) : (
                  <div className="max-h-40 overflow-y-auto pr-1 space-y-0.5" data-testid="email-recent-log">
                    {email.recent.slice(0, 10).map((r) => (
                      <div key={r.id} className="flex items-center gap-2 text-[10.5px] font-mono-clinical">
                        {r.ok
                          ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                          : <XCircle className="w-3 h-3 text-rose-400 shrink-0" />}
                        <span className="text-slate-500 w-14 shrink-0">{fmtRel(r.created_at)}</span>
                        <span className="text-slate-400 w-16 shrink-0 truncate">{r.kind}</span>
                        <span className="text-slate-300 truncate flex-1">{r.to}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* collection counts */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Collection counts</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-1.5" data-testid="health-counts">
                <CountChip label="Users" value={data.counts.users} />
                <CountChip label="Progress" value={data.counts.progress} />
                <CountChip label="Teams" value={data.counts.teams} />
                <CountChip label="Refresh tokens" value={data.counts.refresh_tokens} />
                <CountChip label="Audit logs" value={data.counts.audit_logs} />
                <CountChip label="Stripe events" value={data.counts.stripe_events} />
                <CountChip label="Checkout sessions" value={data.counts.checkout_sessions} />
                <CountChip label="Reset tokens" value={data.counts.password_reset_tokens} />
                <CountChip label="Verify tokens" value={data.counts.email_verification_tokens} />
                <CountChip label="Sessions" value={data.counts.user_sessions} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
